export type ThemeTemplateId = 'indigo' | 'emerald' | 'sapphire' | 'amethyst' | 'obsidian';
export type DisplayMode = 'light' | 'dark' | 'system';

export type ThemeColors = {
  primary: string;
  primaryHover: string;
  secondary: string;
  accent: string;
  bg: string;
  surface: string;
  card: string;
  text: string;
  muted: string;
  border: string;
  borderSubtle: string;
  glassBorder: string;
  glow: string;
  badgeBg: string;
  badgeText: string;
  badgeBorder: string;
  inverse?: string;
};

export type ThemeDefinition = {
  id: ThemeTemplateId;
  name: string;
  tagline: string;
  accentNote: string;
  swatch: {
    primary: string;
    secondary: string;
    accent: string;
    surface: string;
  };
  light: ThemeColors;
  dark: ThemeColors;
};

export type LegacyTheme = {
  name: string;
  colors: {
    primary: string;
    primary700?: string;
    secondary: string;
    accent: string;
    bg: string;
    surface: string;
    text: string;
    muted: string;
    subtle?: string;
    inverse?: string;
  };
  spacing: { xs: string; sm: string; md: string; lg: string; xl?: string };
  radii: { sm: string; md: string; lg?: string; xl?: string; full?: string };
  fonts?: { family?: string; base?: string };
};

// 1. Executive Indigo (Current Default)
export const executiveIndigo: ThemeDefinition = {
  id: 'indigo',
  name: 'Executive Indigo',
  tagline: 'Modern Corporate & Executive Slate',
  accentNote: 'Royal Indigo with Crisp Frosted Glass',
  swatch: {
    primary: '#4f39f6',
    secondary: '#06b6d4',
    accent: '#f97316',
    surface: '#f8fafc'
  },
  light: {
    primary: '#4f39f6',
    primaryHover: '#3b25e6',
    secondary: '#06b6d4',
    accent: '#f97316',
    bg: '#ffffff',
    surface: '#f8fafc',
    card: '#ffffff',
    text: '#0f172a',
    muted: '#64748b',
    border: '#e2e8f0',
    borderSubtle: '#f1f5f9',
    glassBorder: 'rgba(255, 255, 255, 0.7)',
    glow: 'rgba(79, 57, 246, 0.15)',
    badgeBg: '#eef2ff',
    badgeText: '#4338ca',
    badgeBorder: '#c7d2fe',
    inverse: '#ffffff'
  },
  dark: {
    primary: '#6366f1',
    primaryHover: '#4f46e5',
    secondary: '#22d3ee',
    accent: '#fb923c',
    bg: '#090d16',
    surface: '#0f172a',
    card: '#131d31',
    text: '#f8fafc',
    muted: '#94a3b8',
    border: '#1e293b',
    borderSubtle: '#152033',
    glassBorder: 'rgba(255, 255, 255, 0.08)',
    glow: 'rgba(99, 102, 241, 0.25)',
    badgeBg: '#1e1b4b',
    badgeText: '#a5b4fc',
    badgeBorder: '#3730a3',
    inverse: '#090d16'
  }
};

// 2. Emerald Prestige (New Rich Theme 1)
export const emeraldPrestige: ThemeDefinition = {
  id: 'emerald',
  name: 'Emerald Prestige',
  tagline: 'High-Net-Worth Wealth & Corporate Jade',
  accentNote: 'Imperial Emerald with Frosted Mint & Champagne Gold',
  swatch: {
    primary: '#059669',
    secondary: '#0d9488',
    accent: '#f59e0b',
    surface: '#f7fee7'
  },
  light: {
    primary: '#059669',
    primaryHover: '#047857',
    secondary: '#0d9488',
    accent: '#d97706',
    bg: '#fbfdfc',
    surface: '#f0fdf4',
    card: '#ffffff',
    text: '#064e3b',
    muted: '#4b7a68',
    border: '#d1fae5',
    borderSubtle: '#ecfdf5',
    glassBorder: 'rgba(255, 255, 255, 0.8)',
    glow: 'rgba(5, 150, 105, 0.18)',
    badgeBg: '#dcfce7',
    badgeText: '#15803d',
    badgeBorder: '#86efac',
    inverse: '#ffffff'
  },
  dark: {
    primary: '#10b981',
    primaryHover: '#059669',
    secondary: '#14b8a6',
    accent: '#fbbf24',
    bg: '#051b14',
    surface: '#09291f',
    card: '#0f382b',
    text: '#ecfdf5',
    muted: '#86efac',
    border: '#134e3a',
    borderSubtle: '#0c3829',
    glassBorder: 'rgba(255, 255, 255, 0.09)',
    glow: 'rgba(16, 185, 129, 0.25)',
    badgeBg: '#064e3b',
    badgeText: '#6ee7b7',
    badgeBorder: '#047857',
    inverse: '#051b14'
  }
};

// 3. Sapphire Horizon (New Rich Theme 2)
export const sapphireHorizon: ThemeDefinition = {
  id: 'sapphire',
  name: 'Sapphire Horizon',
  tagline: 'Oceanic Deep-Sea & High-Tech Modern Azure',
  accentNote: 'Marine Sapphire with Electric Cyan Sheen',
  swatch: {
    primary: '#0284c7',
    secondary: '#06b6d4',
    accent: '#38bdf8',
    surface: '#f0f9ff'
  },
  light: {
    primary: '#0284c7',
    primaryHover: '#0369a1',
    secondary: '#0891b2',
    accent: '#0284c7',
    bg: '#fcfdfe',
    surface: '#f0f9ff',
    card: '#ffffff',
    text: '#082f49',
    muted: '#477a9a',
    border: '#bae6fd',
    borderSubtle: '#e0f2fe',
    glassBorder: 'rgba(255, 255, 255, 0.85)',
    glow: 'rgba(2, 132, 199, 0.18)',
    badgeBg: '#e0f2fe',
    badgeText: '#0369a1',
    badgeBorder: '#7dd3fc',
    inverse: '#ffffff'
  },
  dark: {
    primary: '#38bdf8',
    primaryHover: '#0ea5e9',
    secondary: '#22d3ee',
    accent: '#7dd3fc',
    bg: '#051624',
    surface: '#082137',
    card: '#0c2d4a',
    text: '#f0f9ff',
    muted: '#7dd3fc',
    border: '#124168',
    borderSubtle: '#0c2e4b',
    glassBorder: 'rgba(255, 255, 255, 0.1)',
    glow: 'rgba(56, 189, 248, 0.28)',
    badgeBg: '#0c4a6e',
    badgeText: '#bae6fd',
    badgeBorder: '#0369a1',
    inverse: '#051624'
  }
};

// 4. Amethyst Royal (New Rich Theme 3)
export const amethystRoyal: ThemeDefinition = {
  id: 'amethyst',
  name: 'Amethyst Royal',
  tagline: 'Imperial Velvet & Ultra-Luxury Plum',
  accentNote: 'Deep Regal Purple with Frosted Lavender Glass',
  swatch: {
    primary: '#7c3aed',
    secondary: '#a855f7',
    accent: '#f43f5e',
    surface: '#faf5ff'
  },
  light: {
    primary: '#7c3aed',
    primaryHover: '#6d28d9',
    secondary: '#9333ea',
    accent: '#e11d48',
    bg: '#fdfcfe',
    surface: '#faf5ff',
    card: '#ffffff',
    text: '#3b0764',
    muted: '#7e6496',
    border: '#e9d5ff',
    borderSubtle: '#f3e8ff',
    glassBorder: 'rgba(255, 255, 255, 0.85)',
    glow: 'rgba(124, 58, 237, 0.18)',
    badgeBg: '#f3e8ff',
    badgeText: '#7e22ce',
    badgeBorder: '#d8b4fe',
    inverse: '#ffffff'
  },
  dark: {
    primary: '#a855f7',
    primaryHover: '#9333ea',
    secondary: '#c084fc',
    accent: '#fb7185',
    bg: '#170627',
    surface: '#230a3a',
    card: '#300f4f',
    text: '#faf5ff',
    muted: '#d8b4fe',
    border: '#4c1d95',
    borderSubtle: '#3b1377',
    glassBorder: 'rgba(255, 255, 255, 0.1)',
    glow: 'rgba(168, 85, 247, 0.28)',
    badgeBg: '#3b0764',
    badgeText: '#e9d5ff',
    badgeBorder: '#6b21a8',
    inverse: '#170627'
  }
};

// 5. Obsidian Onyx & Gold (New Rich Theme 4)
export const obsidianOnyx: ThemeDefinition = {
  id: 'obsidian',
  name: 'Obsidian Onyx & Gold',
  tagline: 'Titanium Carbon Midnight with Radiant Warm Gold',
  accentNote: 'Deep Smoky Glass with Luminous Amber Accents',
  swatch: {
    primary: '#d97706',
    secondary: '#eab308',
    accent: '#f59e0b',
    surface: '#fffbeb'
  },
  light: {
    primary: '#b45309',
    primaryHover: '#92400e',
    secondary: '#ca8a04',
    accent: '#d97706',
    bg: '#ffffff',
    surface: '#fefce8',
    card: '#ffffff',
    text: '#1c1917',
    muted: '#78716c',
    border: '#fde68a',
    borderSubtle: '#fef3c7',
    glassBorder: 'rgba(255, 255, 255, 0.85)',
    glow: 'rgba(217, 119, 6, 0.18)',
    badgeBg: '#fef3c7',
    badgeText: '#92400e',
    badgeBorder: '#fcd34d',
    inverse: '#ffffff'
  },
  dark: {
    primary: '#fbbf24',
    primaryHover: '#f59e0b',
    secondary: '#facc15',
    accent: '#fde047',
    bg: '#0c0a09',
    surface: '#1c1917',
    card: '#24201d',
    text: '#fafaf9',
    muted: '#a8a29e',
    border: '#44403c',
    borderSubtle: '#292524',
    glassBorder: 'rgba(251, 191, 36, 0.12)',
    glow: 'rgba(251, 191, 36, 0.25)',
    badgeBg: '#451a03',
    badgeText: '#fde68a',
    badgeBorder: '#78350f',
    inverse: '#0c0a09'
  }
};

export const THEME_TEMPLATES: Record<ThemeTemplateId, ThemeDefinition> = {
  indigo: executiveIndigo,
  emerald: emeraldPrestige,
  sapphire: sapphireHorizon,
  amethyst: amethystRoyal,
  obsidian: obsidianOnyx
};

export const THEME_TEMPLATE_LIST: ThemeDefinition[] = [
  executiveIndigo,
  emeraldPrestige,
  sapphireHorizon,
  amethystRoyal,
  obsidianOnyx
];

// Legacy backward-compatibility adapters
export const lightTheme: LegacyTheme = {
  name: 'light',
  colors: {
    primary: executiveIndigo.light.primary,
    primary700: executiveIndigo.light.primaryHover,
    secondary: executiveIndigo.light.secondary,
    accent: executiveIndigo.light.accent,
    bg: executiveIndigo.light.bg,
    surface: executiveIndigo.light.surface,
    text: executiveIndigo.light.text,
    muted: executiveIndigo.light.muted,
    subtle: 'rgba(15,23,42,0.6)',
    inverse: '#ffffff'
  },
  spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },
  radii: { sm: '8px', md: '12px', lg: '16px', xl: '24px', full: '9999px' },
  fonts: { family: 'Plus Jakarta Sans, Inter, system-ui, -apple-system, sans-serif', base: '16px' }
};

export const darkTheme: LegacyTheme = {
  name: 'dark',
  colors: {
    primary: executiveIndigo.dark.primary,
    primary700: executiveIndigo.dark.primaryHover,
    secondary: executiveIndigo.dark.secondary,
    accent: executiveIndigo.dark.accent,
    bg: executiveIndigo.dark.bg,
    surface: executiveIndigo.dark.surface,
    text: executiveIndigo.dark.text,
    muted: executiveIndigo.dark.muted,
    subtle: 'rgba(248,250,252,0.72)',
    inverse: '#090d16'
  },
  spacing: { xs: '4px', sm: '8px', md: '16px', lg: '24px', xl: '32px' },
  radii: { sm: '8px', md: '12px', lg: '16px', xl: '24px', full: '9999px' },
  fonts: { family: 'Plus Jakarta Sans, Inter, system-ui, -apple-system, sans-serif', base: '16px' }
};

export const brandTheme = lightTheme;

export const themes = {
  light: lightTheme,
  dark: darkTheme,
  brand: brandTheme,
  indigo: lightTheme,
  emerald: lightTheme,
  sapphire: lightTheme,
  amethyst: lightTheme,
  obsidian: lightTheme
} as const;

export type ThemeName = keyof typeof themes;

export default themes;
